import React, { useMemo } from 'react';
import { Row, Col, Alert, Card, Statistic, Typography, Space, Button } from 'antd';
import type {
  SimulationDetail,
  SimulationReportCarrierSelectionSummaryRecord,
  SimulationReportingTableRecord,
} from '../../store/simulation/types';
import { useSimulationReport, useReportFilterParams } from '../../hooks/simulation/useSimulationReport';
import { SimulationReportFilters } from './SimulationReportFilters';
import { SimulationCard } from './SimulationCard';
import { SimulationReportingTable } from './SimulationReportingTable';
import { palette } from '@shipium-corp/shipium-ui-theme';
import type { CompactPieDatum } from '../../components/reporting/DonutChartCompact';
import { DonutChartCompact } from '../../components/reporting/DonutChartCompact';
import { getColor, renderCaret, shortenLabel } from './utils';
import { usePDF } from 'react-to-pdf';
import { DownloadOutlined } from '@ant-design/icons';
import { useCarrierServiceMethods } from '../../hooks/queries/useCarriersFiltered';
const { Title } = Typography;
export interface SimulationDetailTabProps {
  simulation: SimulationDetail;
}

export const SimulationCarrierSelectionReportTab = ({ simulation }: SimulationDetailTabProps) => {
  const carrierServiceMethodMap = useCarrierServiceMethods();
  const isComplete = useMemo(() => simulation.simulationStatus === 'completed', [simulation]);
  const { toPDF, targetRef } = usePDF({ filename: `${simulation.name}-carrier-selection.pdf` });
  const queryParams = useReportFilterParams();

  const { reportData, loading } = useSimulationReport(simulation, 'carrier-selection-report', queryParams);
  const {
    carrierSelections: carrierSelectionSummaryData,
    carrierServiceMethodCounts,
    ...simulationSummaryData
  } = reportData || {};

  const totalSummaryData: SimulationReportingTableRecord[] = carrierSelectionSummaryData
    ?.map((row: SimulationReportCarrierSelectionSummaryRecord) => {
      return {
        key: row.carrierServiceMethodId,
        dimension: carrierServiceMethodMap[row.carrierServiceMethodId]?.name || row.carrierServiceMethodId,
        simulationValue: row.simulationTotalLabelCost,
        historicalValue: row.historicalTotalLabelCost,
        differenceValue: row.totalLabelCostDifference,
        differencePercentage: row.totalLabelCostDifferencePercentage,
      };
    })
    .sort(
      (a: SimulationReportingTableRecord, b: SimulationReportingTableRecord) => b.simulationValue - a.simulationValue,
    );

  const averageSummaryData: SimulationReportingTableRecord[] = carrierSelectionSummaryData
    ?.map((row: SimulationReportCarrierSelectionSummaryRecord) => {
      return {
        key: row.carrierServiceMethodId,
        dimension: carrierServiceMethodMap[row.carrierServiceMethodId]?.name || row.carrierServiceMethodId,
        simulationValue: row.simulationAverageLabelCost,
        historicalValue: row.historicalAverageLabelCost,
        differenceValue: row.averageLabelCostDifference,
        differencePercentage: row.averageLabelCostDifferencePercentage,
      };
    })
    .sort(
      (a: SimulationReportingTableRecord, b: SimulationReportingTableRecord) => b.simulationValue - a.simulationValue,
    );

  const countSummaryData: SimulationReportingTableRecord[] = Object.values(carrierServiceMethodCounts || {})
    ?.map((row: any) => {
      return {
        key: row.carrierServiceMethodId,
        dimension: carrierServiceMethodMap[row.carrierServiceMethodId]?.name || row.carrierServiceMethodId,
        simulationValue: row.simulationCount,
        historicalValue: row.historicalCount,
      };
    })
    .sort(
      (a: SimulationReportingTableRecord, b: SimulationReportingTableRecord) => b.simulationValue - a.simulationValue,
    );

  const simulationTotal = countSummaryData.reduce((acc, row) => acc + row.simulationValue, 0);
  const historicalTotal = countSummaryData.reduce((acc, row) => {
    return row.key ? acc + (row.historicalValue || 0) : acc;
  }, 0);

  const simulationCountPieData: CompactPieDatum[] = countSummaryData
    ?.map((row: SimulationReportingTableRecord) => {
      return {
        id: row.key,
        label: shortenLabel(carrierServiceMethodMap[row.key]?.name) || row.key,
        key: row.key,
        value: row.simulationValue,
        percent: (row.simulationValue * 100) / simulationTotal / 100,
      };
    })
    .sort((a: CompactPieDatum, b: CompactPieDatum) => b.value - a.value);

  const historicalCountPieData: CompactPieDatum[] = countSummaryData
    ?.map((row: SimulationReportingTableRecord) => {
      return {
        id: row.key,
        label: shortenLabel(carrierServiceMethodMap[row.key]?.name) || row.key,
        key: row.key,
        value: row.historicalValue || 0,
        percent: ((row.historicalValue || 0) * 100) / historicalTotal / 100,
      };
    })
    .sort((a: CompactPieDatum, b: CompactPieDatum) => b.value - a.value);

  const renderSuffix = (val: number, round?: boolean) => {
    if (!val) return null;
    const valStr = round ? Math.round(val).toLocaleString() : val.toLocaleString();
    return (
      <>
        {renderCaret(val, true)}
        <span style={{ marginLeft: 5, fontSize: '0.6em' }}>{valStr}</span>
      </>
    );
  };

  const includeHistorical = historicalTotal > 0;

  return (
    <Row gutter={16}>
      <Col span={24}>
        {!isComplete && (
          <Alert
            type={'warning'}
            showIcon={true}
            description={'The Carrier Selection report will be available once the simulation has completed execution.'}
            message={'Simulation Incomplete'}
          />
        )}
        {isComplete && (
          <>
            <SimulationReportFilters simulation={simulation} />
            <Card bodyStyle={{ paddingTop: 10 }}>
              <Row style={{ margin: 0 }} gutter={32}>
                <Col style={{ marginTop: 0 }} offset={22} span={2}>
                  <Button
                    style={{ float: 'right', marginTop: 0, marginBottom: 10 }}
                    onClick={() => {
                      toPDF();
                    }}
                  >
                    <DownloadOutlined />
                  </Button>
                </Col>
              </Row>
              <div ref={targetRef}>
                <Row gutter={32}>
                  <Col span={8}>
                    <Card
                      bordered={true}
                      style={{ background: '#fafafa' }}
                      loading={loading || carrierSelectionSummaryData === undefined}
                    >
                      <Title level={4}>Total Shipments</Title>
                      <Space size={'large'}>
                        {includeHistorical && (
                          <Statistic
                            title={'Matching Historical Method'}
                            value={simulationSummaryData?.matchingHistoricalCount}
                            precision={0}
                            valueStyle={{ color: palette.Grey.dark }}
                          />
                        )}
                        <Statistic
                          title={'Simulated'}
                          value={simulationSummaryData?.totalSimulationLabelCount}
                          precision={0}
                          valueStyle={{ color: palette.Grey.dark }}
                        />
                      </Space>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card
                      bordered={true}
                      style={{ background: '#fafafa' }}
                      loading={loading || carrierSelectionSummaryData === undefined}
                    >
                      <Title level={4}>Total Cost</Title>
                      <Space size={'large'}>
                        {includeHistorical && (
                          <Statistic
                            title={'Historical'}
                            value={simulationSummaryData?.totalHistoricalLabelCost}
                            suffix={''}
                            prefix={'$'}
                            precision={0}
                            valueStyle={{ color: palette.Grey.darkest }}
                          />
                        )}
                        <Statistic
                          title={'Simulated'}
                          value={simulationSummaryData?.totalSimulationLabelCost}
                          suffix={
                            includeHistorical ? renderSuffix(simulationSummaryData?.totalLabelCostDifference, true) : ''
                          }
                          prefix={'$'}
                          precision={0}
                          valueStyle={{
                            color: getColor(simulationSummaryData?.totalLabelCostDifference, includeHistorical),
                          }}
                        />
                      </Space>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card
                      bordered={true}
                      style={{ background: '#fafafa' }}
                      loading={loading || carrierSelectionSummaryData === undefined}
                    >
                      <Title level={4}>Average Label Cost</Title>
                      <Space size={'large'}>
                        {includeHistorical && (
                          <Statistic
                            title={'Historical'}
                            value={simulationSummaryData?.averageHistoricalLabelCost}
                            precision={2}
                            prefix={'$'}
                            valueStyle={{ color: palette.Grey.dark }}
                          />
                        )}
                        <Statistic
                          title={'Simulated'}
                          value={simulationSummaryData?.averageSimulationLabelCost}
                          suffix={
                            includeHistorical ? renderSuffix(simulationSummaryData?.averageLabelCostDifference) : ''
                          }
                          precision={2}
                          prefix={'$'}
                          valueStyle={{
                            color: getColor(simulationSummaryData?.averageLabelCostDifference, includeHistorical),
                          }}
                        />
                      </Space>
                    </Card>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={24}>&nbsp;</Col>
                </Row>

                <SimulationCard
                  loading={loading}
                  heading={'Carrier Selection Shipment Counts'}
                  bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
                >
                  <Row>
                    {includeHistorical && (
                      <Col span={12}>
                        <div style={{ padding: 10 }}>
                          <Title type={'secondary'} level={5} style={{ textAlign: 'center', marginBottom: -10 }}>
                            Historical Shipments
                          </Title>
                          <DonutChartCompact data={historicalCountPieData} />
                        </div>
                      </Col>
                    )}
                    <Col span={includeHistorical ? 12 : 24}>
                      <div style={{ padding: 10 }}>
                        <Title type={'secondary'} level={5} style={{ textAlign: 'center', marginBottom: -10 }}>
                          Simulated Shipments
                        </Title>
                        <DonutChartCompact data={simulationCountPieData} />
                      </div>
                    </Col>
                  </Row>
                  <SimulationReportingTable
                    simulation={simulation}
                    dimensionTitle={'Carrier Service Method'}
                    valueType={'number'}
                    invertColors={true}
                    colorDifference={true}
                    showDifference={false}
                    showPercentageOfTotal={true}
                    records={countSummaryData}
                    includeHistorical={includeHistorical}
                  />
                </SimulationCard>

                <SimulationCard
                  loading={loading}
                  heading={'Total Label Cost'}
                  bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
                >
                  <SimulationReportingTable
                    simulation={simulation}
                    showDifference={true}
                    colorDifference={true}
                    dimensionTitle={'Carrier Service Method'}
                    valueType={'currency'}
                    records={totalSummaryData}
                    includeHistorical={includeHistorical}
                  />
                </SimulationCard>

                <SimulationCard
                  loading={loading}
                  heading={'Average Label Cost'}
                  bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
                >
                  <SimulationReportingTable
                    simulation={simulation}
                    colorDifference={true}
                    showDifference={true}
                    dimensionTitle={'Carrier Service Method'}
                    valueType={'currency'}
                    records={averageSummaryData}
                    includeHistorical={includeHistorical}
                  />
                </SimulationCard>
              </div>
            </Card>
          </>
        )}
      </Col>
    </Row>
  );
};
